import '.cd{display:inline-flex;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.e2{gap:0.375rem;}.ssr-plugin'
import '.hp{padding:0.375rem 0.625rem;}.ssr-plugin'
import '.f9{border-radius:0.5rem;}.ssr-plugin'
import '.hq{border:1px solid var(--gray-200);}.ssr-plugin'
import '.v{background-color:var(--palette-white);}.ssr-plugin'
import '.hr{max-width:12rem;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.ax{justify-content:center;}.ssr-plugin'
import '.bc{flex-shrink:0;}.ssr-plugin'
import '.fy{background:none;}.ssr-plugin'
import '.c4{border:none;}.ssr-plugin'
import '.l{padding:0px;}.ssr-plugin'
import '.a1{cursor:pointer;}.ssr-plugin'
import '.bx{color:var(--gray-300);}.ssr-plugin'
import '.fb{width:1rem;}.ssr-plugin'
import '.fc{height:1rem;}.ssr-plugin'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { MAX_POST_MESSAGE_LENGTH } from '@app/components/blocks/discussions/constants'
import { Text } from '@app/components/core/typography/Text'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { forwardRef, useCallback, useState } from 'react'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { styled } from 'inlines'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFile, faFilePdf, faImage } from '@fortawesome/pro-solid-svg-icons'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'

const MAX_FILES = 5
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
]

export type ReplyTextBoxProps = {
  onSumbit: (text: string, files: File[]) => Promise<boolean>
  onBlur?: () => void
}

export const ReplyTextBox = forwardRef<HTMLInputElement, ReplyTextBoxProps>(
  ({ onSumbit, onBlur }, ref) => {
    const [language] = useLanguage()
    const translations = i18n.languages[language].app.post
    const [text, setText] = useState('')
    const [postMedia, setPostMedia] = useState<File[]>([])
    const isAtMaxLength = text.length >= MAX_POST_MESSAGE_LENGTH

    const clearForm = useCallback(() => {
      setText('')
      setPostMedia([])
    }, [])

    const onBlurCallback = useCallback(() => {
      if (text.trim() === '' && postMedia.length === 0) {
        onBlur?.()
      }
    }, [text, postMedia.length, onBlur])

    const onSubmitCallback = useCallback(() => {
      if (text.trim() === '') {
        return
      }
      void onSumbit(text.trim(), postMedia).then((success) => {
        if (success) {
          clearForm()
        }
      })
    }, [text, postMedia, onSumbit, clearForm])

    const addFiles = useCallback((newFiles: File[]) => {
      setPostMedia((prev) => {
        const combined = [...prev, ...newFiles]
        const seen = new Set<string>()
        const unique = combined.filter((file) => {
          const key = `${file.name}-${file.size}`
          if (seen.has(key)) {
            return false
          }
          seen.add(key)
          return true
        })
        return unique.slice(0, MAX_FILES)
      })
    }, [])

    const removeFile = useCallback((index: number) => {
      setPostMedia((prev) => prev.filter((_, i) => i !== index))
    }, [])

    return (
      <Column gap="0.5rem" alignItems="stretch">
        <Row>
          <TextInput
            ref={ref}
            placeholder={translations.typeYourReply}
            text={text}
            setText={(value) => setText(value.slice(0, MAX_POST_MESSAGE_LENGTH))}
            onSubmit={onSubmitCallback}
            submitDisabled={text.trim() === ''}
            maxLength={MAX_POST_MESSAGE_LENGTH}
            errorMessage={isAtMaxLength ? translations.maxCharactersReached : undefined}
            onBlur={onBlurCallback}
            style={{
              flexGrow: 1,
            }}
            fileSelector={{
              filesSelected: postMedia.length,
              maxFiles: MAX_FILES,
              allowedFileTypes: ALLOWED_FILE_TYPES,
              onFileSelectionUpdated: addFiles,
            }}
          />
        </Row>
        {postMedia.length > 0 && (
          <Row gap="0.5rem" style={{ flexWrap: 'wrap' }}>
            {postMedia.map((file, index) => {
              const isPdf = file.type === 'application/pdf'
              const isImg = file.type.startsWith('image/')
              const icon = isPdf ? faFilePdf : isImg ? faImage : faFile
              return (
                <div className="cd t e2 hp f9 hq v hr"
                  key={`${file.name}-${file.lastModified}`}
                >
                  <FontAwesomeIcon
                    icon={icon}
                    style={{
                      width: '0.875rem',
                      height: '0.875rem',
                      color: isPdf
                        ? 'var(--palette-red-400)'
                        : 'var(--brand-secondary)',
                      flexShrink: 0,
                    }}
                  />
                  <Text.Small
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'var(--brand-secondary)',
                    }}
                  >
                    {file.name}
                  </Text.Small>
                  <button className="d t ax bc fy c4 l a1 bx fb fc"
                    type="button"
                    onClick={() => removeFile(index)}
                  >
                    <FontAwesomeIcon
                      icon={faXmark}
                      style={{ width: '0.75rem', height: '0.75rem' }}
                    />
                  </button>
                </div>
              )
            })}
          </Row>
        )}
      </Column>
    )
  },
)

ReplyTextBox.displayName = 'ReplyTextBox'
